'use client';
import { useLayoutEffect, useRef, useCallback } from 'react';

/* ============================================================
   AvatarFlap — the avatar cell.

   NO SPLIT-FLAP. Faces don't bisect well: a seam through the middle of a
   real photo reads as a rendering bug, not as mechanism. This cell is a
   single whole rounded-square image at all times.

   What survives from the flap version is the MOTION, not the mechanism:
     - Normally STATIC. An avatar rarely changes, so a plain re-render or
       a data refresh just shows `src`, no animation.
     - On a WEEK CHANGE it does a slot-machine SPIN: riffles through the
       `pool` of league avatars, decelerating, then lands on the real
       `src`. Each swap is a fast vertical nudge + crossfade -- reads as a
       reel ticking over, with the image never cut in half.

   The spin is triggered by the `spin` prop CHANGING (pass spin={week}), so
   it fires once per week rollover and never on ordinary updates.

   Props:
     src    the real avatar URL to land on. '' => blank cell.
     pool   array of avatar URLs to riffle through during a spin.
     spin   any value; when it CHANGES, run the slot-machine (except first
            mount, which just sets the image). Pass the week number.
     size   px cell size. Matches the row's SplitFlap size EXACTLY so the
            avatar sits on the same line as the text cells -- the old
            size * 1.06 cell was the visible misalignment.
     alt    accessible label
   ============================================================ */

const SWAP_MS = 90;

export default function AvatarFlap({
  src = '', pool = [], spin = null, size = 22, alt = '',
}) {
  const refs = useRef(null);
  const shownSrc = useRef(null);
  const lastSpin = useRef(undefined);
  const timers = useRef([]);

  const setNode = useCallback((el) => {
    if (!el) return;
    refs.current = {
      base: el.querySelector('.av-img.av-base'),
      incoming: el.querySelector('.av-img.av-incoming'),
    };
  }, []);

  useLayoutEffect(() => {
    const r = refs.current;
    if (!r) return;

    const setImg = (node, url) => {
      if (url) { node.src = url; node.style.visibility = 'visible'; }
      else { node.removeAttribute('src'); node.style.visibility = 'hidden'; }
    };
    const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const goal = src || '';
    const firstMount = shownSrc.current === null;
    const spinChanged = lastSpin.current !== undefined && spin !== lastSpin.current;
    lastSpin.current = spin;

    if (firstMount || reduced || !spinChanged) {
      if (!firstMount && !reduced && goal !== shownSrc.current) {
        swap(r, setImg, goal, timers, SWAP_MS);
      } else {
        setImg(r.base, goal);
        r.base.style.opacity = '1';
        r.base.style.transform = 'translateY(0)';
        r.incoming.style.opacity = '0';
      }
      shownSrc.current = goal;
      return () => clearTimers();
    }

    // SPIN: slot-machine through the pool, decelerate, land on goal
    clearTimers();
    const reel = buildReel(pool, goal);
    let i = 0;
    const n = reel.length;
    const stepFor = (k) => {
      const t = k / n;
      const ease = 0.5 - 0.5 * Math.cos(Math.PI * Math.min(t * 1.15, 1));
      return 55 + ease * 190; // ~55ms fast -> ~245ms slow near the end
    };
    const runStep = () => {
      if (i >= n) { shownSrc.current = goal; return; }
      const next = reel[i];
      swap(r, setImg, next, timers, Math.min(SWAP_MS, 70));
      shownSrc.current = next;
      const d = stepFor(i);
      i += 1;
      const t = setTimeout(runStep, d);
      timers.current.push(t);
    };
    runStep();

    return () => clearTimers();
  }, [src, spin, pool]);

  return (
    <span
      className="av-cell"
      ref={setNode}
      role="img"
      aria-label={alt}
      style={{ width: size, height: size }}
    >
      <img className="av-img av-base" alt="" />
      <img className="av-img av-incoming" alt="" />
      <style>{AV_CSS}</style>
    </span>
  );
}

// One reel tick: the outgoing image lifts and fades while the incoming one
// rises into place. Fast enough to read as a mechanical advance, but the
// avatar is never cut -- it's always one whole face.
function swap(r, setImg, next, timers, ms) {
  setImg(r.incoming, next);

  r.incoming.style.transition = 'none';
  r.incoming.style.opacity = '0';
  r.incoming.style.transform = 'translateY(38%)';
  void r.incoming.offsetHeight;

  r.base.style.transition = `opacity ${ms}ms linear, transform ${ms}ms cubic-bezier(.4,.1,.3,1)`;
  r.incoming.style.transition = `opacity ${ms}ms linear, transform ${ms}ms cubic-bezier(.4,.1,.3,1)`;
  r.base.style.opacity = '0';
  r.base.style.transform = 'translateY(-38%)';
  r.incoming.style.opacity = '1';
  r.incoming.style.transform = 'translateY(0)';

  // settle: promote the incoming image to base so the next tick starts clean
  const t = setTimeout(() => {
    setImg(r.base, next);
    r.base.style.transition = 'none';
    r.base.style.opacity = '1';
    r.base.style.transform = 'translateY(0)';
    r.incoming.style.transition = 'none';
    r.incoming.style.opacity = '0';
  }, ms + 10);
  timers.current.push(t);
}

function buildReel(pool, goal) {
  const others = pool.filter((u) => u && u !== goal);
  const shuffled = shuffle(others);
  const spins = [];
  const target = 11 + Math.floor(Math.random() * 3);
  for (let k = 0; k < target; k++) {
    spins.push(shuffled[k % Math.max(1, shuffled.length)] || goal);
  }
  spins.push(goal);
  return spins;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* Rounded square, same footprint and radius language as a text cell, but
   undivided. overflow:hidden clips the reel motion to the cell. */
const AV_CSS = `
.av-cell{
  position:relative; display:inline-block; overflow:hidden;
  border-radius:5px; background:#16181c; flex:0 0 auto;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.05);
}
.av-img{
  position:absolute; inset:0; width:100%; height:100%;
  object-fit:cover; display:block; will-change:opacity,transform;
}
.av-img.av-incoming{ opacity:0; }
@media (prefers-reduced-motion: reduce){
  .av-img{ transition:none !important; transform:none !important; }
}
`;
